import React from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { useParams, useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useAppDispatch, RootState } from '../../store';
import apis from '../../utils/apis';
import { formatRoleNameForDisplay } from '../../utils/roleFormatter';
import DetailFrame from '../shared/Frames/DetailFrame';
import Button from '../shared/TailwindComponents/Button';
import Badge from '../shared/TailwindComponents/Badge';
import { showFeedbackModal } from '../../store/actions/UserFeedbackActions';
import { showModal, hideModal } from '../../store/actions/modalActions';
import { PiGearLight, PiLockKeyLight, PiWarningCircleLight } from 'react-icons/pi';
import * as PiIcons from 'react-icons/pi';
import GlassCard from '../shared/brand/GlassCard';
import EmptyState from '../shared/TailwindComponents/EmptyState';
import Skeleton from '../shared/TailwindComponents/Skeleton';
import AdminDetailHeader from './AdminDetailHeader';
import { ADMIN_ROLES_SECTION_PATH } from './adminRoutes';
import useRoleDetail from './useRoleDetail';
import { readErrorMessage } from '../../utils/errorMessage';

// Icon mapping for permission sections
const sectionIconMap: Record<string, string> = {
  'Dashboard': 'PiDashboard',
  'Tasks': 'PiCheckSquare',
  'Calendar': 'PiCalendar',
  'Messages': 'PiChat',
  'Clients': 'PiUsers',
  'Lawyers': 'PiUserCircle',
  'Projects': 'PiTreeStructure',
  'Matters': 'PiBriefcase',
  'Files': 'PiFiles',
  'Research': 'PiMagnifyingGlass',
  'Tables': 'PiGridFour',
  'Analytics': 'PiChartBar',
  'Data Sources': 'PiDatabase',
  'Finder': 'PiUserSwitch',
  'Templates': 'PiFileText',
  'People': 'PiUsers',
  'Departments': 'PiBuilding',
  'Teams': 'PiUsersThree',
  'Finance': 'PiCoinVertical',
};

const getIconByName = (name: string): JSX.Element => {
  const normalizedName = name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
  const spacedName = name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  const iconKey = sectionIconMap[normalizedName] || sectionIconMap[spacedName] || sectionIconMap[name] || 'PiGear';
  const IconComponent = (PiIcons as any)[iconKey + 'Light'] || (PiIcons as any)[iconKey];
  return IconComponent ? <IconComponent className="h-4 w-4" /> : <PiGearLight className="h-4 w-4" />;
};

// Permission display constants
const HIDDEN_PERMISSION_SECTIONS = ['dashboard', 'research', 'client_pipeline', 'analytics', 'messages', 'finance', 'review', 'notifications', 'finder'];

const SECTION_DISPLAY_ORDER = [
  'tasks', 'calendar',
  'clients', 'matters', 'files',
  'tables', 'templates', 'data_sources', 
  'people', 'departments', 'teams',
];

const CATEGORY_ORDER = ['core', 'workspace', 'utilities', 'organization'];

const CATEGORY_LABELS: Record<string, string> = {
  core: 'Core',
  workspace: 'Workspace',
  utilities: 'Utilities',
  organization: 'Organization',
};

const formatSectionName = (name: string): string => {
  return name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

const formatPermissionLabel = (permission: { name: string; description?: string }): string => {
  if (permission.description) return permission.description;
  const parts = permission.name.split(':');
  const action = parts[1] || parts[0];
  return action.charAt(0).toUpperCase() + action.slice(1);
};

export interface AdminRoleDetailProps {
  /** The role to show. Falls back to the route's `:roleId`/`:detailId`. */
  roleId?: string;
  /** Rendered inside the settings shell - no `DetailFrame` of its own. */
  embedded?: boolean;
}

const AdminRoleDetail: React.FC<AdminRoleDetailProps> = ({
  roleId: roleIdProp,
  embedded = false,
}) => {
  const { t } = useTranslation();
  const params = useParams<{ roleId?: string; detailId?: string }>();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const userInfo = useSelector((state: RootState) => state.user.userInfo);

  // As a prop (section sub-view), as `:detailId` (/settings/admin-roles/x) or
  // as `:roleId` (the old top-level route).
  const roleId = roleIdProp || params.detailId || params.roleId || '';

  // Which endpoint owns this role, and how both payloads become one model,
  // lives in `roleDetailService` - this view is endpoint-agnostic.
  const { role, permissions, loading, error, reload } = useRoleDetail(roleId);

  /** Built-in roles are the catalogue's; nothing here may edit them. */
  const isBuiltIn = !!role && !role.is_custom;

  const handleEditRole = () => {
    if (!role || isBuiltIn) return;
    const orgType =
      (userInfo as any)?.organization_type || (userInfo as any)?.OrganizationType || 'law_firm';
    dispatch(showModal({
      showModal: true,
      type: 'adminEditRole',
      role,
      message: t('Edit role'),
      orgType,
      onConfirm: async () => {
        dispatch(showFeedbackModal({
          showModal: true,
          message: t('Role updated successfully'),
          modalType: 'success',
          duration: 3000
        }));
        reload();
      },
      onCancel: () => dispatch(hideModal()),
    }));
  };

  const handleAssignRole = async () => {
    if (!role) {
      dispatch(showFeedbackModal({
        showModal: true,
        message: t('Role not loaded'),
        modalType: 'error',
        duration: 3000
      }));
      return;
    }

    dispatch(showModal({
      showModal: true,
      type: 'adminAssignRole',
      roleId: role.id,
      message: t('Assign role'),
      onConfirm: async (selectedUserId: string) => {
        if (!selectedUserId) {
          dispatch(showFeedbackModal({
            showModal: true,
            message: t('Please select a user'),
            modalType: 'warning',
            duration: 3000
          }));
          return;
        }

        try {
          const res = await apis.post(`/admin/users/${selectedUserId}/roles`, {
            role_name: role.name,
          });

          if (res.data?.success) {
            dispatch(showFeedbackModal({
              showModal: true,
              message: t('Role assigned successfully'),
              modalType: 'success',
              duration: 3000
            }));
          }
        } catch (err: any) {
          dispatch(showFeedbackModal({
            showModal: true,
            message: readErrorMessage(err, t('Failed to assign role')),
            modalType: 'error',
            duration: 4000
          }));
        }
      }
    }));
  };

  /**
   * One frame, two hosts - see `AdminUserEdit`. Standalone it is a page with a
   * `DetailFrame`; inside the settings shell it is a sub-view of the Roles
   * section, so it brings only a breadcrumb back to the list.
   */
  type FrameAction = {
    label: string;
    onClick: () => void;
    variant: 'primary';
    /** Built-in roles keep their edit control visible but inert. */
    disabled?: boolean;
    title?: string;
  };

  const frame = (crumb: string, body: React.ReactNode, actions?: FrameAction[]) =>
    embedded ? (
      <div className="flex h-full min-h-0 flex-col gap-4">
        <AdminDetailHeader
          backTo={ADMIN_ROLES_SECTION_PATH}
          sectionLabel={t('Roles & permissions')}
          recordLabel={crumb}
          actions={actions?.map((action) => (
            <Button
              key={action.label}
              variant="primary"
              size="small"
              disabled={action.disabled}
              title={action.title}
              onClick={action.onClick}
            >
              {action.label}
            </Button>
          ))}
        />
        {body}
      </div>
    ) : (
      <DetailFrame
        breadcrumbs={[
          { name: t('Settings'), href: '/settings', current: false },
          { name: t('Roles'), href: ADMIN_ROLES_SECTION_PATH, current: false },
          { name: crumb, href: '#', current: true },
        ]}
        // DetailFrame has no disabled state for header actions, so a control
        // that cannot act is not offered there at all.
        actions={actions?.filter((a) => !a.disabled)}
      >
        {body}
      </DetailFrame>
    );

  if (loading) {
    return frame(
      t('Loading...'),
      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto">
        <Skeleton variant="form" />
        <Skeleton variant="grid" rows={6} />
      </div>
    );
  }

  /**
   * A failed load explains itself and offers the way forward - Retry first,
   * because the usual cause is transient. Never a spinner that outlives the
   * request.
   */
  if (error || !role) {
    return frame(
      t('Not found'),
      <div className="min-h-0 flex-1 overflow-y-auto">
        <EmptyState
          icon={roleId ? <PiWarningCircleLight /> : <PiLockKeyLight />}
          headline={roleId ? t('Could not load this role') : t('Role not found')}
          subtext={error || t('It may have been deleted, or the link is out of date.')}
          button={
            roleId
              ? { label: t('Retry'), onClick: reload }
              : {
                  label: t('Back to roles'),
                  onClick: () => history.push(ADMIN_ROLES_SECTION_PATH),
                }
          }
        />
        {roleId && (
          <div className="flex justify-center">
            <Button variant="tertiary" size="small" onClick={() => history.push(ADMIN_ROLES_SECTION_PATH)}>
              {t('Back to roles')}
            </Button>
          </div>
        )}
      </div>
    );
  }

  // Build the list of sections to render. Prefer the role's OWN permission
  // objects (authoritative - the custom-role GET returns
  // {id, name, description, section_id}). Reconstructing from the permission
  // catalogue drops any permission that lives in a hidden section, which is what
  // collapsed 8 assigned permissions down to 1.
  let rolePermissions: Array<{ id?: string | number; description?: string; name: string; category?: string; permissions: Array<{ id: number; name: string; description?: string }> }>;

  const sortSections = (
    list: Array<{ id?: string | number; description?: string; name: string; category?: string; permissions: Array<{ id: number; name: string; description?: string }> }>,
  ) =>
    list.sort((a, b) => {
      const idxA = SECTION_DISPLAY_ORDER.indexOf(a.name.toLowerCase());
      const idxB = SECTION_DISPLAY_ORDER.indexOf(b.name.toLowerCase());
      return (idxA === -1 ? 999 : idxA) - (idxB === -1 ? 999 : idxB);
    });

  if (role.permission_objects && role.permission_objects.length > 0) {
    // Catalogue lookup: permission id → its section (name + category), used only
    // for grouping/labelling. A permission missing from the catalogue still
    // renders (it lands in a generic bucket) rather than being dropped.
    const permMeta = new Map<number, { sectionName: string; category?: string }>();
    permissions.forEach(section => {
      section.permissions.forEach(p => {
        permMeta.set(p.id, { sectionName: section.name, category: (section as any).category });
      });
    });

    const bySection = new Map<string, { id?: string | number; description?: string; name: string; category?: string; permissions: Array<{ id: number; name: string; description?: string }> }>();
    role.permission_objects.forEach(p => {
      const meta = permMeta.get(p.id);
      const sectionName = meta?.sectionName || 'other';
      const category = meta?.category || 'core';
      if (!bySection.has(sectionName)) {
        bySection.set(sectionName, { name: sectionName, category, permissions: [] });
      }
      bySection.get(sectionName)!.permissions.push({ id: p.id, name: p.name, description: p.description });
    });

    rolePermissions = sortSections(Array.from(bySection.values()));
  } else {
    // Fallback for roles without permission objects (e.g. the static catalogue):
    // reconstruct from permission_ids / permission_names against the catalogue.
    let permissionIdsToShow = new Set<number>();
    const rawPermissionNames = role.permission_names || [];

    if (role.permission_ids && role.permission_ids.length > 0) {
      permissionIdsToShow = new Set(role.permission_ids);
    } else if (rawPermissionNames && rawPermissionNames.length > 0) {
      permissions.forEach(section => {
        section.permissions.forEach(perm => {
          if (rawPermissionNames.includes(perm.name)) {
            permissionIdsToShow.add(perm.id);
          } else {
            const rawNameLower = rawPermissionNames.join('|').toLowerCase();
            const permNameLower = perm.name.toLowerCase();
            if (rawNameLower.includes(permNameLower.split(/\s+/)[0])) {
              permissionIdsToShow.add(perm.id);
            }
          }
        });
      });
    }

    rolePermissions = sortSections(
      permissions
        .filter(s => !HIDDEN_PERMISSION_SECTIONS.includes(s.name.toLowerCase()))
        .map(section => ({
          ...section,
          permissions: section.permissions.filter(p => permissionIdsToShow.has(p.id)),
        }))
        .filter(section => section.permissions.length > 0),
    );
  }

  const groupedRolePermissions = CATEGORY_ORDER
    .map(cat => ({
      category: cat,
      label: CATEGORY_LABELS[cat],
      sections: rolePermissions.filter(s => (s.category || 'core') === cat),
    }))
    .filter(g => g.sections.length > 0);

  const heading = role.display_name || formatRoleNameForDisplay(role.name);

  return frame(
    heading,
    <div className={clsx('min-h-0 flex-1 overflow-y-auto', !embedded && 'px-4 py-6')}>
        {/* Title, built-in marker and description. Embedded, the breadcrumb
            already names the role, so the heading is the description's anchor
            rather than a second title. */}
        {(!embedded || isBuiltIn || role.description) && (
          <div className="mb-6">
            <div className="mb-2 flex items-center gap-2">
              {!embedded && (
                <h1 className="text-2xl font-medium text-fg-strong">{heading}</h1>
              )}
              {isBuiltIn && (
                <Badge
                  color="gray"
                  icon={<PiLockKeyLight />}
                  tooltip={t('Built-in roles ship with the platform and cannot be changed.')}
                >
                  {t('Built-in role')}
                </Badge>
              )}
            </div>
            {role.description && (
              <p className="text-sm text-fg-muted">{role.description}</p>
            )}
          </div>
        )}

        {/* Permissions Grid */}
        {groupedRolePermissions.length === 0 ? (
          <EmptyState
            density="compact"
            icon={<PiLockKeyLight />}
            headline={t('No permissions assigned')}
            subtext={
              isBuiltIn
                ? t('This built-in role grants nothing in the sections shown here.')
                : t('This role grants nothing yet — edit it to add permissions.')
            }
          />
        ) : (
          <div className="space-y-8">
            {groupedRolePermissions.map(group => (
              <div key={group.category}>
                <h2 className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-4">
                  {group.label}
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {group.sections.map(section => (
              <GlassCard
                key={String(section.id ?? section.name)}
                icon={getIconByName(section.name)}
                title={formatSectionName(section.name)}
                subtitle={section.description}
                className="hover:shadow-md transition-shadow"
              >
                {/* Permissions list with checkmarks */}
                <div className="space-y-2">
                  {section.permissions.map(perm => (
                    <div key={perm.id} className="flex items-start gap-2">
                      <span className="flex-shrink-0 w-5 h-5 text-success-600 dark:text-success-500 mt-0.5">
                      <PiIcons.PiCheckCircleLight className='text-success-600'/>
                      </span>
                      <span className="text-xs text-gray-700 dark:text-gray-300">
                        {formatPermissionLabel(perm)}
                      </span>
                    </div>
                  ))}
                </div>

                {/* Permission count */}
                <div className="mt-4 pt-3 border-t border-hairline-soft">
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {section.permissions.length} {section.permissions.length === 1 ? t('permission') : t('permissions')}
                  </p>
                </div>
              </GlassCard>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
    </div>,
    [
      // Editing is a custom-role affordance. Assigning is not editing the
      // role, so it stays available for built-ins too.
      {
        label: t('Edit role'),
        onClick: handleEditRole,
        variant: 'primary' as const,
        disabled: isBuiltIn,
        title: isBuiltIn ? t('Built-in roles cannot be edited') : undefined,
      },
      { label: t('Assign role'), onClick: handleAssignRole, variant: 'primary' as const },
    ]
  );
};

export default AdminRoleDetail;
